import {
  categorySubtreeHasProducts,
  compareCategoryOrder,
  getSecondaryCategories,
} from '~/utils/store/categoryTree'

const FOOTER_PRIMARY_CATEGORY_LIMIT = 3
const FOOTER_SECONDARY_CATEGORY_LIMIT = 5

/**
 * @description 将分类节点转为顶栏 / 页脚展示结构
 * @param {Object|null|undefined} category
 * @param {Object} [options]
 * @param {string} [options.keyPrefix]
 * @param {string} [options.label]
 * @returns {{ key: string, label: string, mobileLabel: string, category: Object|null, categoryId: string|number, categories: Array<Object> }|null}
 */
function mapCategoryToNavSection(category, options = {}) {
  const categoryId = category?.categoryId
  if (!categoryId) return null

  const { keyPrefix = 'category', label = '' } = options
  const displayLabel = label || category.categoryName || ''
  if (!displayLabel) return null

  return {
    key: `${keyPrefix}-${categoryId}`,
    label: displayLabel,
    mobileLabel: displayLabel,
    category,
    categoryId,
    categories: getSecondaryCategories(category),
  }
}

/**
 * @description 解析页脚展示用的一级分类及二级分类
 * @param {Array<Object>} categoryList
 * @returns {Array<{ key: string, label: string, mobileLabel: string, category: Object|null, categoryId: string|number, categories: Array<Object> }>}
 */
export function mapTopCategoryNavSections(categoryList) {
  return (Array.isArray(categoryList) ? categoryList : [])
    .map((category) => mapCategoryToNavSection(category))
    .filter(Boolean)
}

/**
 * @description 页脚分类：最多 3 个有上架商品的一级分类；每列最多 5 个有上架商品的二级分类
 * @param {Array<Object>} categoryList
 * @returns {Array<{ key: string, label: string, mobileLabel: string, category: Object|null, categoryId: string|number, categories: Array<Object> }>}
 */
export function mapFooterCategoryNavSections(categoryList) {
  const primaries = (Array.isArray(categoryList) ? categoryList : [])
    .filter((category) => category?.categoryId && categorySubtreeHasProducts(category))
    .sort(compareCategoryOrder)

  const sections = []

  for (const category of primaries) {
    if (sections.length >= FOOTER_PRIMARY_CATEGORY_LIMIT) break

    const secondaries = getSecondaryCategories(category).slice(0, FOOTER_SECONDARY_CATEGORY_LIMIT)
    if (!secondaries.length) continue

    const section = mapCategoryToNavSection(category)
    if (!section) continue

    sections.push({
      ...section,
      categories: secondaries,
    })
  }

  return sections
}

/**
 * @description 按聚合页接口配置生成顶栏一级分类展示结构
 * @param {Array<Object>} categoryList
 * @param {Array<Object>} aggregatePages
 * @returns {Array<{ key: string, label: string, mobileLabel: string, category: Object|null, categoryId: string|number, categories: Array<Object> }>}
 */
export function mapAggregateTopCategoryNavSections(categoryList, aggregatePages) {
  const categoryById = new Map(
    (Array.isArray(categoryList) ? categoryList : [])
      .filter((category) => category?.categoryId)
      .map((category) => [String(category.categoryId), category])
  )

  return (Array.isArray(aggregatePages) ? aggregatePages : [])
    .slice()
    .sort((left, right) => {
      const leftId = Number(left?.categoryId || left?.pageKey || 0)
      const rightId = Number(right?.categoryId || right?.pageKey || 0)
      if (Number.isFinite(leftId) && Number.isFinite(rightId) && leftId !== rightId) {
        return leftId - rightId
      }
      return String(left?.categoryId || left?.pageKey || '').localeCompare(
        String(right?.categoryId || right?.pageKey || ''),
        undefined,
        { numeric: true }
      )
    })
    .map((page) => {
      const categoryId = String(page?.categoryId || page?.pageKey || '')
      if (!categoryId) return null

      const category = categoryById.get(categoryId) || {
        categoryId,
        categoryName: page?.pageName || '',
      }

      return mapCategoryToNavSection(category, {
        keyPrefix: 'aggregate-category',
        label: page?.pageName || category.categoryName || '',
      })
    })
    .filter(Boolean)
}
